
import React from "react";
import { Loader2 } from "lucide-react";
import { SubscriptionCard } from "./subscription/SubscriptionCard";
import { ResourceUsageCard } from "./subscription/ResourceUsageCard";
import { AiMessageUsageCard } from "./subscription/AiMessageUsageCard";
import { useSubscriptionData } from "@/hooks/use-subscription-data";
import { useResourceUsage } from "@/hooks/use-resource-usage";
import { useIsMobile } from "@/hooks/use-mobile";

interface SubscriptionTabProps {
  userId: string | undefined;
}

export const SubscriptionTab: React.FC<SubscriptionTabProps> = ({ userId }) => {
  const { data: subscriptionData, isLoading: isSubscriptionLoading } = useSubscriptionData(userId);
  const { data: resourceUsage, isLoading: isResourceUsageLoading } = useResourceUsage(userId);
  const isMobile = useIsMobile();

  if (isSubscriptionLoading || isResourceUsageLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Calculate subscription status
  if (subscriptionData?.package) {
    // Handle unlimited subscription (no end_date)
    if (!subscriptionData.end_date) {
      return (
        <div className={`space-y-${isMobile ? "4" : "6"}`}>
          <SubscriptionCard 
            subscriptionData={subscriptionData}
            remainingPercentage={100}
            isExpired={false}
            isAboutToExpire={false}
            isUnlimited={true}
          />
          
          <ResourceUsageCard 
            resourceUsage={resourceUsage}
            packageData={subscriptionData.package}
            resourceWarnings={[]}
          />

          <AiMessageUsageCard enableRealTimeUpdates={false} />
        </div>
      );
    }

    const endDate = new Date(subscriptionData.end_date);
    const startDate = new Date(subscriptionData.start_date);
    const currentDate = new Date();
    
    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = currentDate.getTime() - startDate.getTime();
    const remainingPercentage = Math.max(0, Math.min(100, 100 - (elapsed / totalDuration * 100)));
    
    const isExpired = currentDate > endDate;
    const isAboutToExpire = !isExpired && (endDate.getTime() - currentDate.getTime()) < (7 * 24 * 60 * 60 * 1000);

    // Calculate resource warnings
    const resourceWarnings: string[] = [];
    if (resourceUsage && subscriptionData.package) {
      const packageData = subscriptionData.package;
      
      if (resourceUsage.userBotsCount >= packageData.max_user_bots) {
        resourceWarnings.push("You've reached the maximum number of user bots");
      }
      if (resourceUsage.premiumBotAccountsCount >= packageData.max_premium_bots) {
        resourceWarnings.push("You've reached the maximum number of premium bot connections");
      }
      if (resourceUsage.propBotAccountsCount >= packageData.max_prop_bots) {
        resourceWarnings.push("You've reached the maximum number of prop bot connections");
      }
      if (resourceUsage.cspAccountsCount >= packageData.max_csp_accounts) {
        resourceWarnings.push("You've reached the maximum number of CTrader profiles");
      }
      if (resourceUsage.tradingAccountsCount >= packageData.max_trading_accounts) {
        resourceWarnings.push("You've reached the maximum number of trading accounts");
      }
    }

    return (
      <div className={`space-y-${isMobile ? "4" : "6"}`}>
        <SubscriptionCard 
          subscriptionData={subscriptionData}
          remainingPercentage={remainingPercentage}
          isExpired={isExpired}
          isAboutToExpire={isAboutToExpire}
          isUnlimited={false}
        />
        
        <ResourceUsageCard 
          resourceUsage={resourceUsage}
          packageData={subscriptionData.package}
          resourceWarnings={resourceWarnings}
        />

        <AiMessageUsageCard enableRealTimeUpdates={false} />
      </div>
    );
  }

  return (
    <div className={`space-y-${isMobile ? "4" : "6"}`}>
      <SubscriptionCard 
        subscriptionData={null} 
        remainingPercentage={0} 
        isExpired={false} 
        isAboutToExpire={false} 
        isUnlimited={false} 
      />
      
      <AiMessageUsageCard enableRealTimeUpdates={false} />
    </div>
  );
};
